import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Lock, Sparkles, X } from "lucide-react";

import { useWsNavigate } from "../../hooks/useWsNavigate";
import { useAuth } from "../../contexts/AuthContext";
import { useFeature } from "../../hooks/useFeature";
import useEntitlements from "../../hooks/useEntitlements";
import { lsRead, lsWrite } from "../../utils/wsPrefs";
import { discoveryQuestionsAPI } from "../../services/api";

// ─── Fallback questions (shown while the API loads or if generation hasn't run) ─

export const FALLBACK_QUESTIONS = [
  "How many conversations met their objective?",
  "Which conversations had the lowest customer satisfaction?",
  "What are the most common reasons conversations failed?",
  "Which agents are performing best this week?",
  "Show conversations where the agent did not follow the script.",
  "Which scenarios have the highest failure rate?",
];

const SOURCE_OPTIONS = [
  { value: "", label: "All sources" },
  { value: "observation", label: "Observability calls" },
  { value: "scenario", label: "Scenario runs" },
];

const PAGE_SIZE = 3;

// Co-Pilot lives behind the Growth plan (see Plan::FEATURE_KEYS / FeatureRoute).
const COPILOT_FEATURE = "observability.discovery";
const BILLING_UPGRADE_PATH = "/settings/billing?upgrade=1";
const DISMISSED_PREF = "copilot-suggestions-dismissed";

// ─── Collapsed nudge ────────────────────────────────────────────────────────
// What a dismissed carousel leaves behind on pay-as-you-go. Deliberately not
// nothing: these questions are the clearest statement of what Growth buys, so
// hiding them entirely would cost the upsell. One line is cheap enough to
// live with forever, and it's the only way back to the full carousel.

const CollapsedNudge = ({ onShow, onUpgrade }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-border bg-muted/30 px-4 py-2.5">
    <Sparkles className="h-4 w-4 flex-shrink-0 text-amber-400" />
    <p className="text-sm text-muted-foreground">
      <span className="font-medium text-foreground">Co-Pilot suggestions</span>
      {" · "}
      Ask questions about your calls in plain English — a Growth feature.
    </p>
    <div className="ml-auto flex items-center gap-1">
      <button
        onClick={onUpgrade}
        className="rounded-lg px-2.5 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary/10"
      >
        Upgrade
      </button>
      <button
        onClick={onShow}
        className="rounded-lg px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        Show
      </button>
    </div>
  </div>
);

// ─── Discovery carousel ─────────────────────────────────────────────────────

const DiscoveryCarousel = ({ clientId }) => {
  const navigate = useWsNavigate();
  const { accessLoaded } = useAuth();
  const canUseCopilot = useFeature(COPILOT_FEATURE);
  const { isPayg, loading: entitlementsLoading } = useEntitlements();

  const [questions, setQuestions] = useState([]);
  // `generating` drives the spinner; the ref is the in-flight guard. They are
  // separate because triggerGenerate runs from an async .then that closes over
  // a previous render — reading the state there would see a stale value and
  // refuse to generate for the workspace the user has already switched to.
  const [generating, setGenerating] = useState(false);
  const generatingRef = useRef(false);
  const [sourceFilters, setSourceFilters] = useState({});
  const [page, setPage] = useState(0);
  // Lazily initialised: reading this in an effect instead would render the
  // full-height carousel on the first commit and collapse it right after —
  // a layout jump on every dashboard load, for the users who asked to stop
  // seeing the section. The effect below only handles later clientId changes.
  const [dismissed, setDismissed] = useState(() =>
    clientId ? lsRead(DISMISSED_PREF, clientId) : false,
  );

  // Locked = the workspace can see what Co-Pilot would answer, but can't run it.
  const locked = !canUseCopilot;

  useEffect(() => {
    setDismissed(clientId ? lsRead(DISMISSED_PREF, clientId) : false);
  }, [clientId]);

  useEffect(() => {
    // `locked` is meaningless until access lands (useFeature reports false
    // while loading), and acting on it early both flashes the upsell at
    // entitled workspaces and fetches the questions twice.
    if (!clientId || !accessLoaded) return;
    // A collapsed section renders no questions, so fetching them is pure waste.
    // Read the stored preference directly: the sibling effect that syncs
    // `dismissed` keys on the same clientId, so relying on its state here would
    // use the OUTGOING workspace's preference for one commit. `dismissed` stays
    // in the deps so pressing Show still re-runs this and fetches for real,
    // rather than leaving the user on the generic fallback list.
    if (locked && lsRead(DISMISSED_PREF, clientId)) return;
    // Drop the outgoing workspace's questions now rather than leaving them on
    // screen under the new workspace's id — acting on one of those would send
    // updateStatus(newClientId, oldQuestionId).
    setQuestions([]);
    setPage(0);
    setSourceFilters({});
    generatingRef.current = false;
    setGenerating(false);
    let ignore = false;
    discoveryQuestionsAPI
      .getQuestions(clientId)
      .then((res) => {
        if (ignore) return;
        const qs = res?.questions || [];
        setQuestions(qs);
        // Generation is an LLM call. Never spend it building a nudge for a
        // workspace that can't open the answers — the static fallbacks below
        // make the same pitch for free. `locked` is the entitlement itself, so
        // it is the right and only question to ask here; see the note on
        // accessLoaded above for the window where it can briefly be stale.
        if (qs.length === 0 && !locked) triggerGenerate(clientId, () => ignore);
      })
      .catch(() => {});
    // A slow response from the previous workspace must not land on this one.
    return () => {
      ignore = true;
    };
  }, [clientId, accessLoaded, locked, dismissed]); // eslint-disable-line react-hooks/exhaustive-deps

  // `id` and `isStale` are passed in rather than read from the closure: this is
  // the slowest call in the component (an LLM round trip), so it is the one most
  // likely to resolve after the workspace has already changed underneath it.
  const triggerGenerate = (id, isStale) => {
    if (generatingRef.current) return;
    generatingRef.current = true;
    setGenerating(true);
    discoveryQuestionsAPI
      .generate(id)
      .then((res) => {
        if (isStale()) return;
        setQuestions(res?.questions || []);
      })
      .catch(() => {})
      .finally(() => {
        // Staleness-guarded on both counts: once the workspace has moved on,
        // this call owns neither the in-flight guard nor the spinner, and
        // clearing either would release the one the live request is using.
        // The effect resets both when it re-runs, so nothing is left stuck.
        if (isStale()) return;
        generatingRef.current = false;
        setGenerating(false);
      });
  };

  const handleMarkRead = (q) => {
    if (String(q.id).startsWith("fb-")) return;
    discoveryQuestionsAPI.updateStatus(clientId, q.id, "read").catch(() => {});
    setQuestions((prev) =>
      prev.map((x) => (x.id === q.id ? { ...x, status: "read" } : x)),
    );
  };

  const handleSkip = (q) => {
    if (String(q.id).startsWith("fb-")) return;
    discoveryQuestionsAPI
      .updateStatus(clientId, q.id, "skipped")
      .catch(() => {});
    setQuestions((prev) =>
      prev.map((x) => (x.id === q.id ? { ...x, status: "skipped" } : x)),
    );
  };

  const setDismissedPref = (val) => {
    if (clientId) lsWrite(DISMISSED_PREF, clientId, val);
    setDismissed(val);
  };

  const goToUpgrade = () => navigate(BILLING_UPGRADE_PATH);

  if (!accessLoaded) return null;
  // Mirrors FeatureRoute's ordering (App.js) for consistency. Note this is
  // behaviourally a no-op: both guards return null, and useEntitlements reports
  // isPayg === false while loading, so the !isPayg check below already covers
  // every loading render.
  if (locked && entitlementsLoading) return null;
  // A workspace without the feature that isn't on pay-as-you-go has nothing to
  // upgrade *to* — "Upgrade to Growth" would be wrong copy. Drop the section,
  // matching how the sidebar hides Co-Pilot for the same workspaces.
  if (locked && !isPayg) return null;

  if (locked && dismissed) {
    return (
      <CollapsedNudge
        onShow={() => setDismissedPref(false)}
        onUpgrade={goToUpgrade}
      />
    );
  }

  const sorted = [...questions].sort((a, b) => {
    const order = { unread: 0, read: 1, skipped: 2 };
    return (order[a.status] ?? 3) - (order[b.status] ?? 3);
  });

  const displayList =
    sorted.length > 0
      ? sorted
      : FALLBACK_QUESTIONS.map((q, i) => ({
          id: `fb-${i}`,
          question: q,
          status: "unread",
          source: "standard",
        }));

  const totalPages = Math.ceil(displayList.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(0, totalPages - 1));
  const visible = displayList.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE,
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <Sparkles className="h-5 w-5 text-amber-400" />
          <h2 className="text-sm font-semibold text-foreground">Try these</h2>
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-600">
            Beta
          </span>
          {locked && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary">
              <Lock className="h-2.5 w-2.5" />
              Growth
            </span>
          )}
          {generating && (
            <span className="text-xs text-muted-foreground animate-pulse">
              Generating...
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground mr-1">
            {safePage + 1} / {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.max(0, safePage - 1))}
            disabled={safePage === 0}
            className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-muted-foreground hover:bg-muted/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
            disabled={safePage >= totalPages - 1}
            className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-muted-foreground hover:bg-muted/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowRight className="h-4 w-4" />
          </button>
          {locked && (
            <button
              onClick={() => setDismissedPref(true)}
              aria-label="Hide Co-Pilot suggestions"
              title="Hide Co-Pilot suggestions"
              className="ml-0.5 p-1.5 rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {visible.map((q) => {
          const src = sourceFilters[q.id] || "";
          const isRead = !locked && q.status === "read";
          return (
            <div
              key={q.id}
              className={`bg-card rounded-xl border p-4 flex flex-col justify-between hover:shadow-sm transition-all group ${isRead ? "border-border opacity-70" : "border-border"}`}
            >
              <p
                className={`text-sm leading-relaxed mb-5 font-medium ${isRead ? "text-muted-foreground" : "text-foreground"}`}
              >
                {q.question}
                {isRead && (
                  <span className="ml-2 text-xs text-gray-300 font-normal">
                    read
                  </span>
                )}
              </p>
              {/* Locked workspaces get the upgrade at the point of the click.
                  Sending them to /copilot just to meet UpgradeGate there is a
                  round trip that ends in a wall. */}
              {locked ? (
                <button
                  onClick={goToUpgrade}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/10"
                >
                  <Lock className="h-3.5 w-3.5" />
                  Unlock with Growth
                </button>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        handleMarkRead(q);
                        const params = new URLSearchParams({ q: q.question });
                        if (src) params.set("source", src);
                        navigate(`/copilot?${params.toString()}`);
                      }}
                      className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      Analyse
                    </button>
                    <select
                      value={src}
                      onChange={(e) =>
                        setSourceFilters((p) => ({
                          ...p,
                          [q.id]: e.target.value,
                        }))
                      }
                      className="text-sm text-muted-foreground border border-border rounded-lg px-2 py-2 bg-card hover:border-input focus:outline-none focus:ring-1 focus:ring-primary/20 cursor-pointer"
                    >
                      {SOURCE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={() => handleSkip(q)}
                    className="text-xs text-gray-300 hover:text-muted-foreground transition-colors opacity-0 group-hover:opacity-100"
                  >
                    Skip
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DiscoveryCarousel;
